import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Auto-bootstrap FCM push notifications on Capacitor native shell only.
 *
 * Behavior:
 *  - On the web (PWA, browser preview): no-op. Web push is handled separately
 *    by PushOptIn.jsx using the existing VAPID flow.
 *  - On native Capacitor (Android, iOS): the FIRST time we see the user
 *    logged-in, we silently check permission. If `granted` we register and
 *    POST the token to `/push/fcm/register`. If `prompt`, we leave it alone —
 *    Settings page exposes the opt-in button that calls requestPermissions().
 *  - When the user logs out, we drop the token from the backend.
 */
export function useFcmPush() {
  const { user } = useAuth();
  const sentTokenRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let listeners = [];

    async function init() {
      // Capacitor detection — works without importing @capacitor/core eagerly.
      const isNative = !!(window.Capacitor?.isNativePlatform?.());
      if (!isNative || !user) return;

      let PushNotifications;
      try {
        ({ PushNotifications } = await import("@capacitor/push-notifications"));
      } catch (e) {
        // Plugin missing in this build — silently skip.
        return;
      }

      const handleRegistration = async (token) => {
        const value = token?.value;
        if (!value || !isMounted) return;
        if (sentTokenRef.current === value) return; // dedup
        sentTokenRef.current = value;
        try {
          await api.post("/push/fcm/register", { token: value, platform: "android" });
        } catch (e) {
          // Backend may be temporarily unreachable — fine to retry on next launch.
          // eslint-disable-next-line no-console
          console.warn("[fcm] backend register failed:", e?.message);
        }
      };

      const handleError = (err) => {
        // eslint-disable-next-line no-console
        console.warn("[fcm] registration error:", err);
      };

      try {
        const r1 = await PushNotifications.addListener("registration", handleRegistration);
        const r2 = await PushNotifications.addListener("registrationError", handleError);
        listeners.push(r1, r2);

        const perm = await PushNotifications.checkPermissions();
        if (perm.receive === "granted") {
          await PushNotifications.register();
        }
      } catch (e) {
        // No-op when google-services.json is missing or FCM isn't configured.
      }
    }

    init();

    return () => {
      isMounted = false;
      listeners.forEach((l) => { try { l?.remove?.(); } catch { /* noop */ } });
    };
  }, [user]);
}
